import type { PayrollBatchRow } from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import { formatDate } from '@/utils';
import { Flex } from 'antd';
import { formatBDT, toAmount } from '../shared/payroll.helpers';

type Props = {
  open: boolean;
  row?: PayrollBatchRow | null;
  onClose: () => void;
};

type Entry = [label: string, value: unknown];

/**
 * Breakdown keys that are not money. Daily rows put their cycle facts into the
 * structure breakdown, so they are formatted here instead of shown as BDT.
 */
const NON_MONEY: Partial<Record<string, (value: unknown) => string>> = {
  cycle_no: (value) => `Cycle ${value}`,
  total_days: (value) => `${value} days`,
  present_days: (value) => `${value} days`,
  payable_days: (value) => `${value} days`,
  cycle_start_date: (value) => formatDate(value as string, 'DD MMM YYYY'),
  cycle_end_date: (value) => formatDate(value as string, 'DD MMM YYYY'),
};

/** `{ house_rent: 500 }` → `[['house_rent', 500]]`, nothing for an empty object. */
const toEntries = (data?: Record<string, unknown> | null): Entry[] =>
  Object.entries(data || {});

/**
 * Read-only view of how one employee's final salary was reached: structure,
 * grade benefits, additions, bonuses, deductions and tax.
 */
const SalaryBreakdownDrawer = ({ open, row, onClose }: Props) => {
  // Tax arrives as its own field but comes off the final salary, so it is
  // listed with the other deductions.
  const hasTax = row?.tax_amount != null && row.tax_amount !== '';
  const deductions: Entry[] = [
    ...toEntries(row?.fixed_deduction),
    ...(hasTax ? ([['tax', row?.tax_amount]] as Entry[]) : []),
  ];

  const sections: { title: string; entries: Entry[]; isDeduction?: boolean }[] =
    [
      {
        title: 'Daily Pay',
        entries: Object.entries(row?.daily_pay_breakdown || {}),
      },
      {
        title: 'Base Salary Structure',
        entries: toEntries(row?.base_salary_structure_breakdown),
      },
      {
        title: 'Grade Benefits',
        entries: toEntries(row?.grade_benefits_breakdown),
      },
      { title: 'Other Additions', entries: toEntries(row?.fixed_addition) },
      {
        title: 'Bonus Additions',
        entries: (row?.bonus_addition || []).map((bonus): Entry => [
          bonus.title,
          bonus.amount,
        ]),
      },
      { title: 'Deductions', entries: deductions, isDeduction: true },
    ].filter((section) => section.entries.length > 0);

  return (
    <CustomDrawer
      open={open}
      onClose={onClose}
      title="Salary Breakdown"
      width={550}
    >
      <div className="py-2 pr-2">
        {sections.map((section) => (
          <BreakdownSection key={section.title} {...section} />
        ))}

        {!sections.length && (
          <div className="text-center py-10 text-white/50 italic">
            No detailed breakdown available for this record.
          </div>
        )}

        {row?.adjustment_note && (
          <div className="mb-8 rounded-xl border border-theme-gray-700 bg-theme-slate-900/40 p-4">
            <div className="text-white/50 text-xs uppercase tracking-wider mb-1">
              Adjustment Note
            </div>
            <p className="text-white/90 text-sm">{row.adjustment_note}</p>
          </div>
        )}

        {row?.net_salary && (
          <div className="mt-6 pt-6 border-t border-theme-gray-700">
            <Flex
              justify="space-between"
              align="center"
              className="px-6 py-5 rounded-2xl bg-gradient-to-r from-theme-primary/20 via-theme-primary/5 to-transparent border border-theme-primary/30"
            >
              <div className="flex flex-col">
                <span className="text-white/50 text-xs font-black uppercase tracking-widest mb-1">
                  Net Payable Total
                </span>
                <span className="text-white text-xl font-bold">
                  Final Salary
                </span>
              </div>
              <span className="text-theme-primary text-3xl font-black tracking-tighter">
                {formatBDT(row.net_salary)}
              </span>
            </Flex>
          </div>
        )}
      </div>
    </CustomDrawer>
  );
};

/** One titled group of label / amount lines. */
const BreakdownSection = ({
  title,
  entries,
  isDeduction = false,
}: {
  title: string;
  entries: Entry[];
  isDeduction?: boolean;
}) => (
  <div className="mb-8 last:mb-0">
    <div className="flex items-center gap-3 mb-4">
      <div className="h-px flex-1 bg-gradient-to-r from-theme-primary/50 to-transparent" />
      <h4 className="text-white/70 text-[11px] font-black uppercase tracking-[0.2em]">
        {title}
      </h4>
      <div className="h-px flex-1 bg-gradient-to-l from-theme-primary/50 to-transparent" />
    </div>

    <div className="grid grid-cols-1 gap-3">
      {entries.map(([key, value], index) => {
        const asText = NON_MONEY[key];
        const amount = toAmount(value);
        return (
          <Flex
            key={`${key}-${index}`}
            justify="space-between"
            align="center"
            className="bg-theme-slate-900/40 p-4 rounded-xl border border-theme-gray-700/30 hover:border-theme-primary/20 transition-colors"
          >
            <span className="text-white/90 text-sm font-medium capitalize">
              {key.replace(/_/g, ' ')}
            </span>
            <span
              className={`text-lg font-bold tracking-tight ${
                asText
                  ? 'text-white/80'
                  : isDeduction
                    ? 'text-theme-error-500'
                    : 'text-theme-primary'
              }`}
            >
              {asText
                ? asText(value)
                : `${isDeduction && amount > 0 ? '-' : ''}${formatBDT(amount)}`}
            </span>
          </Flex>
        );
      })}
    </div>
  </div>
);

export default SalaryBreakdownDrawer;
